export interface Vector3Config {
  x: number
  y: number
  z: number
}

/** Set false for the player build to enforce scene-specific camera bounds. */
export const AUTHORING_CAMERA_MODE = false

export const assetUrl = (path: string): string =>
  `${import.meta.env.BASE_URL}${path.replace(/^\//, '')}`

export const appConfig = {
  assets: {
    bedroom: assetUrl('models/bedroom_optimized.glb'),
    kitchen: assetUrl('models/kitchen_optimized.glb'),
    exterior: assetUrl('models/elina_exterior_optimized.glb'),
    exteriorEnvironment: assetUrl('environments/elina-evening-puresky-1k.hdr'),
    phoneKeyTicks: assetUrl('audio/phone-keyboard-ticks.wav'),
    woosh: assetUrl('audio/woosh.mp3'),
    animeMatcaps: {
      toon: assetUrl('matcaps/anime-toon.png'),
      room: assetUrl('matcaps/anime-room-soft.png'),
      fabric: assetUrl('matcaps/anime-fabric-soft.png'),
      terracotta: assetUrl('matcaps/anime-terracotta-clay.png'),
    },
  },
  renderer: {
    clearColor: 0x050711,
    maxPixelRatio: 2,
    exposure: 1.02,
  },
  camera: {
    fov: 30,
    mobileFov: 45,
    near: 0.05,
    far: 60,
    position: { x: 2.203248, y: 2.376573, z: -0.960936 } satisfies Vector3Config,
    target: { x: 0, y: 1.3, z: 2.2 } satisfies Vector3Config,
    intro: {
      position: { x: 0.002156, y: 1.45924, z: -3.245357 } satisfies Vector3Config,
      target: { x: 0, y: 1.3, z: 2.2 } satisfies Vector3Config,
      delay: 0.35,
      duration: 3.2,
      ease: 'power2.inOut',
    },
  },
  controls: {
    minDistance: 0.55,
    maxDistance: 11,
    minPolarAngle: 0.28,
    maxPolarAngle: Math.PI * 0.53,
    minAzimuthAngle: -Infinity,
    maxAzimuthAngle: Infinity,
    rotateSpeed: 0.45,
    enablePan: false,
    enableZoom: false,
  },
  authoringControls: {
    minDistance: 0.05,
    maxDistance: Infinity,
    minPolarAngle: 0,
    maxPolarAngle: Math.PI,
    minAzimuthAngle: -Infinity,
    maxAzimuthAngle: Infinity,
    rotateSpeed: 0.45,
    enablePan: true,
    enableZoom: true,
  },
  scene: {
    background: 0x050711,
    fogColor: 0x070a18,
    fogDensity: 0.018,
    ambientColor: 0x8196c2,
    ambientIntensity: 0.9,
  },
  lighting: {
    // Large, soft sources aimed at the TV wall. The focused wash keeps the
    // imported painting readable without flattening the night-time palette.
    wallWash: {
      color: 0xffd7b5,
      intensity: 4.8,
      width: 4.6,
      height: 2.35,
      position: { x: 0, y: 2.35, z: 1.28 } satisfies Vector3Config,
      target: { x: 0, y: 1.82, z: 2.86 } satisfies Vector3Config,
    },
    paintingFill: {
      color: 0xffeedb,
      intensity: 3.2,
      width: 1.7,
      height: 1.45,
      position: { x: -1.05, y: 2.3, z: 1.72 } satisfies Vector3Config,
      target: { x: -1.05, y: 2.16, z: 2.86 } satisfies Vector3Config,
    },
    clockGlow: {
      color: 0x72baff,
      intensity: 2.2,
      distance: 2.25,
      decay: 2,
      position: { x: -0.073, y: 2.215, z: 2.56 } satisfies Vector3Config,
      haloPosition: { x: -0.073, y: 2.215, z: 2.79 } satisfies Vector3Config,
      haloSize: 1.08,
      // Set this back to 0.62 to restore the visible blue ring.
      haloOpacity: 0,
    },
    bedsideLamps: {
      color: 0xffbd73,
      intensity: 3.6,
      distance: 3.1,
      decay: 2,
      positions: [
        { x: -1.28, y: 1.38, z: -2.45 },
        { x: 1.28, y: 1.38, z: -2.45 },
      ] satisfies readonly Vector3Config[],
    },
  },
  kitchen: {
    // Phones use a wider lens so the narrow viewport retains more of each
    // authored kitchen shot. Desktop keeps fov 30.
    mobileFov: 45,
    // Camera pose 1 is installed behind the black dot transition. The timeline
    // then plays cameraTour from poses 2 through 5 and stays at the last pose.
    camera: {
      position: { x: 0.341875, y: 1.064765, z: 1.106144 } satisfies Vector3Config,
      target: { x: 0.029674, y: 0.82, z: 0.4389 } satisfies Vector3Config,
    },
    cameraTour: {
      steps: [
        {
          position: { x: 0.111758, y: 0.953462, z: 1.416154 } satisfies Vector3Config,
          target: { x: 0.012723, y: 0.82, z: 0.445464 } satisfies Vector3Config,
          duration: 2.2,
          ease: 'power2.inOut',
        },
        {
          position: { x: -0.635028, y: 1.271018, z: 0.378764 } satisfies Vector3Config,
          target: { x: -0.199753, y: 0.82, z: 0.5911 } satisfies Vector3Config,
          duration: 2.8,
          ease: 'power2.inOut',
        },
        {
          position: { x: -1.344857, y: 1.372659, z: 0.978318 } satisfies Vector3Config,
          target: { x: -0.483985, y: 0.82, z: 0.939013 } satisfies Vector3Config,
          duration: 2.8,
          ease: 'power2.inOut',
        },
        {
          position: { x: -2.4895, y: 2.937633, z: 1.438714 } satisfies Vector3Config,
          target: { x: -0.595581, y: 0.82, z: 0.792309 } satisfies Vector3Config,
          duration: 4,
          ease: 'power2.inOut',
        },
      ],
    },
    packedCamera: {
      start: {
        position: { x: -1.705376, y: 1.28284, z: 1.252244 } satisfies Vector3Config,
        target: { x: -0.837473, y: 0.82, z: 0.818634 } satisfies Vector3Config,
      },
      move: {
        position: { x: -2.238125, y: 2.625155, z: 1.333628 } satisfies Vector3Config,
        target: { x: -0.623673, y: 0.82, z: 0.782606 } satisfies Vector3Config,
        duration: 3,
        ease: 'power2.inOut',
      },
    },
    tableStates: {
      packed: {
        hideGroups: [
          'GiftBag',
          'Sweater',
          'HeartMug',
          'GameboyWatchStand',
          'Scarf',
          'OccultBook',
          'Perfume',
          'Bracelet',
        ],
        hideNodes: [
          'AUTH_Sweater_Text_BLIND',
          'AUTH_Sweater_Text_IN_LOVE',
          'AUTH_Mug_Text_Elinas_Bruce',
          'AUTH_Book_Cover_Text',
        ],
        showGroups: ['PackedBox', 'PackedCarKey'],
      },
    },
    scene: {
      background: 0x11131a,
      fogColor: 0x171a20,
      fogDensity: 0.012,
      ambientColor: 0xc9bba7,
      ambientIntensity: 1.05,
    },
    lighting: {
      wallWash: {
        color: 0xffd2a3,
        intensity: 4.2,
        width: 3.4,
        height: 2.4,
        position: { x: -0.2, y: 3.15, z: 0.84 } satisfies Vector3Config,
        target: { x: -0.2, y: 0.8, z: 0.84 } satisfies Vector3Config,
      },
      paintingFill: {
        color: 0xe4eeff,
        intensity: 2.3,
        width: 2.2,
        height: 1.5,
        position: { x: -0.2, y: 2.1, z: 2.35 } satisfies Vector3Config,
        target: { x: -0.2, y: 0.82, z: 0.82 } satisfies Vector3Config,
      },
      clockGlow: {
        color: 0xffb86f,
        intensity: 0.65,
        distance: 2.2,
        decay: 2,
        position: { x: 0.48, y: 1.35, z: 0.72 } satisfies Vector3Config,
        haloPosition: { x: 0.48, y: 1.35, z: 0.72 } satisfies Vector3Config,
        haloSize: 0.1,
        haloOpacity: 0,
      },
      bedsideLamps: {
        color: 0xffc27e,
        intensity: 1.25,
        distance: 2.5,
        decay: 2,
        positions: [
          { x: -0.95, y: 1.4, z: 1.18 },
          { x: 0.58, y: 1.3, z: 0.55 },
        ] satisfies readonly Vector3Config[],
      },
    },
  },
  exterior: {
    // The opening pose is installed under black, fully revealed and briefly
    // held before cameraArrival moves into the doorstep composition.
    camera: {
      position: { x: 11.106754, y: 15.211765, z: 31.346519 } satisfies Vector3Config,
      target: { x: -0.854435, y: 2.55, z: -1.1879 } satisfies Vector3Config,
    },
    cameraArrivalHoldMs: 650,
    cameraArrival: {
      position: { x: 0.177575, y: 2.911463, z: 2.625013 } satisfies Vector3Config,
      target: { x: -0.091535, y: 2.55, z: 1.614527 } satisfies Vector3Config,
      duration: 3,
      ease: 'power2.inOut',
    },
    // Derived from the four approved camera-edge poses around the fixed target.
    // Polar: bottom 40.0273deg -> top 71.8745deg.
    // Azimuth: right -6.2942deg -> left 33.0730deg.
    controls: {
      authoring: {
        minDistance: 0.05,
        maxDistance: Infinity,
        minPolarAngle: 0,
        maxPolarAngle: Math.PI,
        minAzimuthAngle: -Infinity,
        maxAzimuthAngle: Infinity,
        rotateSpeed: -0.45,
        enablePan: true,
        enableZoom: true,
      },
      player: {
        // The four approved edge poses share this exact radius. Equal min/max
        // plus enableZoom=false prevents player dolly in either direction.
        minDistance: 1.10641658,
        maxDistance: 1.10641658,
        minPolarAngle: 0.6986085026,
        maxPolarAngle: 1.254446268,
        minAzimuthAngle: -0.109854731,
        maxAzimuthAngle: 0.5772331497,
        rotateSpeed: -0.45,
        enablePan: false,
        enableZoom: false,
      },
    },
    gameplay: {
      doormat: {
        // Negative Z moves the mat upward on screen toward the front door from
        // the authored arrival camera.
        slideBy: { x: 0, y: 0, z: -0.58 } satisfies Vector3Config,
        lift: 0.028,
        duration: 0.9,
      },
      brokenPot: {
        moveBy: { x: -0.46, y: 0.035, z: -0.14 } satisfies Vector3Config,
        tipRotationZ: -0.34,
        duration: 0.86,
        keyRevealAt: 0.3,
      },
      decoyPot: {
        lift: 0.075,
        wiggleRotationZ: 0.11,
        duration: 0.62,
      },
      hintHighlight: {
        hoverHeight: 0.055,
        duration: 0.62,
      },
      resetDuration: 0.72,
      keyPickup: {
        frontDistance: 0.95,
        flyDuration: 0.84,
        holdDuration: 0.58,
        dockDuration: 0.46,
        frontScale: 1.12,
        dockOffset: { x: 0.82, y: 0.8 },
      },
    },
    environment: {
      // Copied from the authored destruction World's Mapping and Background
      // nodes. Blender's Z panorama rotation maps to Three.js's Y rotation.
      rotationY: -2.2340214252471924,
      backgroundIntensity: 0.34,
      environmentIntensity: 0.42,
    },
    scene: {
      background: 0x53697d,
      fogColor: 0x63788a,
      fogDensity: 0.008,
      ambientColor: 0xdce6f2,
      ambientIntensity: 1.2,
    },
    lighting: {
      wallWash: {
        color: 0xffd3ad,
        intensity: 5.2,
        width: 22,
        height: 18,
        position: { x: 10, y: 13, z: 11 } satisfies Vector3Config,
        target: { x: -0.4, y: 2.4, z: -2.8 } satisfies Vector3Config,
      },
      paintingFill: {
        color: 0xbfd8ff,
        intensity: 2.5,
        width: 18,
        height: 14,
        position: { x: -12, y: 9, z: 8 } satisfies Vector3Config,
        target: { x: -0.4, y: 2.2, z: -2.8 } satisfies Vector3Config,
      },
      clockGlow: {
        color: 0xffad62,
        intensity: 2.1,
        distance: 8,
        decay: 2,
        position: { x: -1.2, y: 2.15, z: -1.55 } satisfies Vector3Config,
        haloPosition: { x: -1.2, y: 2.15, z: -1.55 } satisfies Vector3Config,
        haloSize: 0.1,
        haloOpacity: 0,
      },
      bedsideLamps: {
        color: 0xffc078,
        intensity: 1.8,
        distance: 7,
        decay: 2,
        positions: [
          { x: -2.2, y: 2.3, z: -1.72 },
          { x: 0.05, y: 2.3, z: -1.72 },
        ] satisfies readonly Vector3Config[],
      },
    },
  },
} as const

export type AppConfig = typeof appConfig
